/**
 * Settings > Claims > Customer Claims.
 *
 * Lets a tenant admin choose whether customers may raise their own claims from
 * the customer portal and the public claim page. Existing claims stay visible
 * to customers either way.
 */

import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  useConsumerClaimSettings,
  useUpdateConsumerClaimSettings,
} from '@/admin/hooks/use-consumer-claim-settings'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { toastApiError } from '@/shared/lib/api-error'

export function ConsumerClaimsTab() {
  const { t } = useTranslation()
  const { data, isPending, isError } = useConsumerClaimSettings()
  const update = useUpdateConsumerClaimSettings()

  const onToggle = async (enabled: boolean) => {
    try {
      await update.mutateAsync({ enabled })
      toast.success(
        enabled
          ? t('consumerClaims.enabledToast', 'Customers can now raise claims.')
          : t('consumerClaims.disabledToast', 'Customers can no longer raise claims.'),
      )
    } catch (err) {
      toastApiError(err, t('consumerClaims.saveFailed', 'Failed to save the setting'))
    }
  }

  return (
    <div className="max-w-[560px] py-2">
      <h2 className="mb-1 font-heading text-[18px] text-[var(--ink)]">
        {t('consumerClaims.title', 'Customer claims')}
      </h2>
      <p className="mb-5 text-[13px] leading-relaxed text-[var(--ink-3)]">
        {t(
          'consumerClaims.description',
          'Let customers raise their own claims online. When this is off, customers can still view and follow up on claims they already have.',
        )}
      </p>
      {isPending ? (
        <p className="text-[13px] text-[var(--ink-3)]">
          {t('consumerClaims.loading', 'Loading settings...')}
        </p>
      ) : isError || !data ? (
        <p className="text-[13px] text-[var(--status-error)]">
          {t('consumerClaims.loadError', 'Could not load this setting.')}
        </p>
      ) : (
        <section className="flex items-center justify-between gap-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
          <div>
            <Label
              htmlFor="consumer-claims-enabled"
              className="text-[14px] text-[var(--ink)]"
            >
              {t('consumerClaims.toggleLabel', 'Allow customers to raise claims')}
            </Label>
            <p
              className="mt-0.5 text-[12px] text-[var(--ink-3)]"
              data-testid="consumer-claims-status"
            >
              {!data.available
                ? t(
                    'consumerClaims.unavailableStatus',
                    'Not available for your account. Contact support to turn it on.',
                  )
                : data.enabled
                  ? t('consumerClaims.onStatus', 'Customers can raise claims online.')
                  : t(
                      'consumerClaims.offStatus',
                      'Customers cannot raise claims online.',
                    )}
            </p>
          </div>
          <Switch
            id="consumer-claims-enabled"
            checked={data.available && data.enabled}
            onCheckedChange={onToggle}
            disabled={!data.available || update.isPending}
            data-testid="consumer-claims-toggle"
          />
        </section>
      )}
    </div>
  )
}
